import { Link } from "expo-router"
import React from "react"
import { useTranslation } from "react-i18next"
import { StyleSheet } from "react-native"

import { SignOutButton } from "@/components/Auth"
import { FarmHqLogo } from "@/components/FarmHqLogo"
import { DevOnly } from "@/components/makeFilterComponent"
import {
  canSeedTestDatabase,
  SeedDatabaseOptionsForm,
} from "@/components/SeedDatabaseOptionsForm"
import { getIsUserAuthenticatedFromState } from "@/redux/auth.reducer"
import { useRootSelector } from "@/redux/useRootSelector"
import {
  AppText,
  Box,
  Button,
  Heading,
  ListItem,
  Pressable,
  Row,
  Theme,
  View,
} from "@/theme"

import type { ViewProps } from "@/theme"

const styles = StyleSheet.create({
  logo: {
    height: 80,
    width: "100%",
  },
  root: {
    alignItems: "center",
    display: "flex",
    flex: 1,
    flexDirection: "column",
    margin: "auto",
    maxHeight: 400,
    width: "100%",
  },

  text: {
    marginVertical: Theme.SPACING.$4,
  },
})

export function ErrorPageWrapper({
  children,
  title,
  ...rest
}: ViewProps & {
  title: string
}): React.JSX.Element {
  return (
    <View style={styles.root} {...rest}>
      <FarmHqLogo
        name={"farmhq-logo-inline.png"}
        resizeMode={"contain"}
        style={styles.logo}
      />
      <Heading style={styles.text} variant={"h1"}>
        {title}
      </Heading>
      {children}
    </View>
  )
}

/**
 *
 */
export function NotFound404({
  backButton,
  messageText,
}: {
  backButton?: React.JSX.Element
  messageText?: string
}): React.JSX.Element {
  const { t } = useTranslation("errors")

  return (
    <ErrorPageWrapper id={"notFound"} title={t("notFoundTitle")}>
      <AppText>{messageText ?? t("notFoundMessage")}</AppText>
      <Box my={"$4"}>
        {backButton ?? (
          <Link asChild href={"/"}>
            <Button text={t("home", { ns: "common" })} />
          </Link>
        )}
      </Box>
    </ErrorPageWrapper>
  )
} /**
 *
 */

export function Page500({
  onReset,
}: Partial<Error> & { onReset?: () => void }): React.JSX.Element {
  const isAuthenticated = useRootSelector(getIsUserAuthenticatedFromState)
  const [isSeedingDatabase, setIsSeedingDatabase] = React.useState(false)
  const { t } = useTranslation()
  if (isSeedingDatabase) {
    return (
      <SeedDatabaseOptionsForm onClose={() => setIsSeedingDatabase(false)} />
    )
  }
  return (
    <ErrorPageWrapper id={"page500"} title={"500"}>
      <DevOnly>
        {canSeedTestDatabase() ? (
          <Pressable onPress={() => setIsSeedingDatabase(true)}>
            <ListItem IconComponent={"Database"} text={"Seed Database"} />
          </Pressable>
        ) : null}
      </DevOnly>
      <Row justifyContent={"space-around"}>
        {onReset ? <Button text={t("dismiss")} onPress={onReset} /> : null}
        {isAuthenticated ? <SignOutButton variant={"text"} /> : null}
      </Row>
    </ErrorPageWrapper>
  )
} /**
 *
 */

export function Page404(): React.JSX.Element {
  return (
    <ErrorPageWrapper id={"page404"} title={"404"}>
      <Box m={"auto"}>
        <NotFound404 />
      </Box>
    </ErrorPageWrapper>
  )
}
